<template>
  <div>
    <NavBar></NavBar>
    <div class="row">
      <MarketWatch class="col-md-4 d-none d-md-block"></MarketWatch>
      <div class="col-md-8">
        <div class="funds-container">
          <div class="page-header">
            <h4 class="page-title">
              <i class="bi bi-wallet2"></i>
              Funds Report
            </h4>
          </div>

          <!-- Filter Card -->
          <div class="filter-card">
            <div class="filter-row">
              <div class="filter-item">
                <label for="startDate">From</label>
                <input type="date" class="form-control" :class="inputClass" v-model="start" :max="maxDate" :placeholder="placeholder" />
              </div>
              <div class="filter-item">
                <label for="endDate">To</label>
                <input type="date" class="form-control" :class="inputClass" v-model="end" :max="maxDate" :placeholder="placeholder" />
              </div>
              <div class="filter-actions">
                <button class="btn btn-primary" @click="getReport()">
                  <i class="bi bi-search"></i>
                  <span>Get Report</span>
                </button>
              </div>
            </div>
            <p class="error-msg" v-if="errorMsg">{{ errorMsg }}</p>
          </div>

          <!-- Summary Cards -->
          <div class="summary-section" v-if="data.length > 0">
            <div class="summary-grid">
              <div class="summary-card">
                <div class="summary-icon start">
                  <i class="bi bi-arrow-down-circle"></i>
                </div>
                <div class="summary-content">
                  <span class="summary-label">Start Funds</span>
                  <span class="summary-value">&#8377; {{ data[0].amount.toLocaleString('en-IN') }}</span>
                </div>
              </div>
              <div class="summary-card">
                <div class="summary-icon end">
                  <i class="bi bi-arrow-up-circle"></i>
                </div>
                <div class="summary-content">
                  <span class="summary-label">End Funds</span>
                  <span class="summary-value">&#8377; {{ data[data.length - 1].amount.toLocaleString('en-IN') }}</span>
                </div>
              </div>
              <div class="summary-card">
                <div class="summary-icon roi" :class="getRoiClass()">
                  <i class="bi bi-percent"></i>
                </div>
                <div class="summary-content">
                  <span class="summary-label">ROI</span>
                  <span class="summary-value" :class="getRoiClass()">{{ getPercentageChange(data[0].amount, data[data.length - 1].amount) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Chart Section -->
          <div class="chart-section" v-if="data.length > 0">
            <div class="chart-card">
              <div ref="chartContainer" id="chart-container"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from '@/components/main/NavBar.vue'
import MarketWatch from '@/components/marketwatch/MarketWatch'
import { getVirtualReport } from '@/service/restService'

export default {
  name: 'FundsReport',
  components: {
    NavBar,
    MarketWatch
  },
  data () {
    const today = new Date()
    const todayStr = today.toISOString().slice(0, 10) // '2025-12-21'

    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    const startStr = startOfMonth.toISOString().slice(0, 10) // '2025-12-01'
    return {
      start: startStr,
      end: todayStr,
      errorMsg: '',
      data: [],
      chart: null
    }
  },
  props: {
    placeholder: {
      type: String,
      default: 'Select a date'
    },
    inputClass: {
      type: String,
      default: ''
    }
  },
  mounted () {
    window.addEventListener('resize', this.adjustChartSize)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.adjustChartSize)
    if (this.chart) {
      this.chart.remove()
    }
  },
  methods: {
    adjustChartSize () {
      this.$nextTick(() => {
        const container = this.$refs.chartContainer
        if (!container) return // Guard against missing container
        const rect = container.getBoundingClientRect()
        const availableHeight = Math.min(
          window.innerHeight - rect.top - 20, // 20px padding
          600 // Max height to prevent oversized chart
        )
        const availableWidth = container.clientWidth

        // Ensure height is positive and reasonable
        if (availableHeight > 0) {
          container.style.height = `${availableHeight}px`
          if (this.chart) {
            this.chart.resize(availableWidth, availableHeight)
          }
        } else {
          // Fallback height
          container.style.height = '400px'
          if (this.chart) {
            this.chart.resize(availableWidth, 400)
          }
        }
      })
    },
    getReport () {
      this.errorMsg = ''
      if (!this.start) {
        this.errorMsg = 'Please select start date'
        return
      }
      if (!this.end) {
        this.errorMsg = 'Please select end date'
        return
      }
      const startDate = new Date(this.start)
      const endDate = new Date(this.end)
      if (startDate > endDate) {
        this.errorMsg = 'Start date must be earlier than end date'
        return
      }
      const start1 = this.start
      const end1 = this.end
      getVirtualReport(start1, end1)
        .then((response) => {
          this.data = response.data
          if (this.data.length === 0) {
            this.errorMsg = 'Data not available!'
            return
          }
          this.$nextTick(() => {
            this.initializeChart()
          })
          this.triggerAndroidFunction('REVIEW')
        })
        .catch((error) => {
          this.data = []
          this.errorMsg = error.response.data.message[0] || 'An error occurred'
        })
    },
    initializeChart () {
      const container = this.$refs.chartContainer
      if (!container) {
        console.warn('Chart container not found')
        return
      }
      if (this.chart) {
        this.chart.remove()
      }
      const formattedData = this.data
        .map((item) => {
          if (!item.date || !item.amount) {
            console.warn('Invalid data item:', item)
            return null
          }
          const dateValue = new Date(item.date).getTime() / 1000
          return {
            time: dateValue,
            value: item.amount
          }
        })
        .filter((item) => item !== null)

      const isDarkMode = localStorage.getItem('darkMode') === 'true'
      this.chart = window.LightweightCharts.createChart(this.$refs.chartContainer, {
        layout: {
          backgroundColor: isDarkMode ? '#212529' : '#ffffff',
          textColor: isDarkMode ? '#e0e0e0' : '#333'
        },
        grid: {
          vertLines: { color: isDarkMode ? '#3b3b5c' : '#e1e1e1' },
          horzLines: { color: isDarkMode ? '#3b3b5c' : '#e1e1e1' }
        },
        timeScale: {
          timeVisible: false,
          secondsVisible: false,
          fixLeftEdge: true
        }
      })

      this.lineSeries = this.chart.addLineSeries({
        color: '#2962FF',
        lineWidth: 3
      })

      this.lineSeries.setData(formattedData)
      this.adjustChartSize()
    },
    getPercentageChange (start, end) {
      if (!start || !end) return 'N/A'
      const change = ((end - start) / start) * 100
      return `${change.toFixed(2)}%`
    },
    getRoiClass () {
      if (this.data.length < 2) return ''
      const change = this.data[this.data.length - 1].amount - this.data[0].amount
      return change >= 0 ? 'positive' : 'negative'
    }
  },
  computed: {
    maxDate () {
      const today = new Date()
      return today.toISOString().split('T')[0]
    }
  }
}
</script>

<style scoped>
.funds-container {
  padding: 20px;
  max-width: 900px;
}

.page-header {
  margin-bottom: 20px;
}

.page-title {
  font-weight: 600;
  color: var(--bs-body-color);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-title i {
  color: var(--bs-primary);
}

/* Filter Card */
.filter-card {
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
}

.filter-item {
  flex: 1;
  min-width: 140px;
}

.filter-item label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: var(--bs-secondary-color);
  margin-bottom: 6px;
}

.filter-item .form-control {
  border-radius: 8px;
  border: 1px solid var(--bs-border-color);
  background-color: var(--bs-body-bg);
  color: var(--bs-body-color);
}

.filter-actions {
  display: flex;
  gap: 10px;
}

.filter-actions .btn {
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  padding: 8px 16px;
}

.error-msg {
  color: #dc3545;
  margin-top: 12px;
  margin-bottom: 0;
  font-size: 14px;
}

/* Summary Section */
.summary-section {
  margin-bottom: 20px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.summary-card {
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.summary-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.summary-icon i {
  font-size: 20px;
}

.summary-icon.start {
  background: rgba(13, 110, 253, 0.1);
  color: #0d6efd;
}

.summary-icon.end {
  background: rgba(102, 16, 242, 0.1);
  color: #6610f2;
}

.summary-icon.roi {
  background: rgba(108, 117, 125, 0.1);
  color: #6c757d;
}

.summary-icon.roi.positive {
  background: rgba(11, 218, 81, 0.1);
  color: #0BDA51;
}

.summary-icon.roi.negative {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

.summary-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.summary-label {
  font-size: 12px;
  color: var(--bs-secondary-color);
}

.summary-value {
  font-size: 16px;
  font-weight: 600;
  color: var(--bs-body-color);
}

.summary-value.positive {
  color: #0BDA51;
}

.summary-value.negative {
  color: #dc3545;
}

/* Chart Section */
.chart-section {
  margin-top: 20px;
}

.chart-card {
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

#chart-container {
  width: 100%;
  min-height: 300px;
}

input[type="date"] {
  position: relative;
}

input[type="date"]::-webkit-calendar-picker-indicator {
  cursor: pointer;
  opacity: 0.7;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .funds-container {
    padding: 15px;
  }

  .filter-row {
    flex-direction: column;
  }

  .filter-item {
    width: 100%;
  }

  .filter-actions {
    width: 100%;
  }

  .filter-actions .btn {
    flex: 1;
    justify-content: center;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

  .summary-card {
    padding: 14px;
  }

  .summary-icon {
    width: 40px;
    height: 40px;
  }

  .summary-icon i {
    font-size: 18px;
  }

  .summary-value {
    font-size: 15px;
  }

  #chart-container {
    min-height: 250px;
  }
}

@media (min-width: 992px) {
  #chart-container {
    min-height: 400px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/report/FundsReport.vue